import React, { useContext, useState } from "react";
import StepContainer from "./StepContainer";
import { Field, Form } from "react-final-form";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import type { SxProps, Theme } from "@mui/material/styles";
import { OnboardingContext } from "../../../../store/OnboardingContext";
import {
  bodyTextSx,
  FormBody,
  FormElement,
  formFieldSx,
  incorrectMobileNumberSx,
  secSubmitGoBackSx,
  submitSx,
  submittedMobileNumberSx,
  VerificationCodeInput,
} from "../SignUpForm.styles";
import {
  composeValidators,
  isEmail,
  isEmailRequired,
  isPasswordRequired,
  isPasswordValid,
  isRequired,
} from "../../../../utils/validators";
import {
  isEmailDomainValid,
  getEmailSuggestion,
} from "../../../../utils/emailDomainCheck";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

/**
 * PM5 T2 (money path, batch "onboarding") — converted from MUI v4 to MUI v5+
 * (`@mui/material`). See docs/mui-conversion-guide.md.
 *
 * **AUTH-ADJACENT / SUBSCRIPTIONS ESCALATION CLASS.** This step collects the
 * name, email and password a FarmFLiX account is created from, and hosts the
 * email-verification code entry. Styling-only conversion: every validator,
 * field name, handler and branch is unchanged.
 */

/**
 * v4's `<Grid container spacing={2}>` emitted
 *   `.MuiGrid-spacing-xs-2 { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaced that with `gap` on the container and no padding on the items
 * (guide §2). Rather than re-deriving the flex arithmetic, the port carries
 * v4's own declarations verbatim: `spacing` is omitted (v9 default 0 ⇒
 * `gap: 0px`) and each item keeps the 8px padding itself. Identical by
 * construction, and the 8px container overhang the full-width fields are laid
 * out against survives.
 */
const v4GridContainerSx: SxProps<Theme> = {
  width: "calc(100% + 16px)",
  margin: "-8px",
};
const v4GridItemSx = { padding: "8px" };

// v9's `Grid` types its `sx` as a single `SystemStyleObject`, not `SxProps`, so
// the `sx={[a, b]}` array form does not compile on a Grid. The item padding and
// the shared `formField` rule are merged into one object instead, in that order
// — the same order the two v4 classes were declared in.
const formFieldItemSx: SxProps<Theme> = { ...v4GridItemSx, ...formFieldSx };

/**
 * The two `<Grid item xs={12} sm={12}>` elements in the verification branch sit
 * inside a plain `<form>`, with no `<Grid container>` ancestor. v4 still
 * emitted their `flex-basis` / `max-width` percentages; in v9 a Grid's width
 * comes from CSS custom properties published by a container, so with no
 * container the calc() is invalid and the element falls back to full width.
 * Guide §2: port an orphan item as a Box carrying the literal v4 declarations.
 */
const orphanGridItemSx: SxProps<Theme> = {
  boxSizing: "border-box",
  margin: 0,
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

const SignUpStepDetails = () => {
  const { application, loading, setDetails, verifyEmail } =
    useContext(OnboardingContext);
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);

  const submit = ({
    firstName,
    lastName,
    email,
    password,
  }: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => {
    setDetails(firstName, lastName, email, password);
  };

  const { emailVerificationSentCount } = application!;

  const verifying = emailVerificationSentCount > 0;

  return (
    <StepContainer>
      <StepContainer.Title title="Your Details" />
      <StepContainer.Body>
        {!verifying && (
          <Form onSubmit={submit}>
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                <FormBody>
                  <Grid container sx={v4GridContainerSx}>
                    <Grid size={{ xs: 12, sm: 6 }} sx={formFieldItemSx}>
                      <Field name="firstName" validate={isRequired}>
                        {({ input, meta }) => (
                          /* PARITY RESTORE (guide §2, "TextField default
                             variant"): v4 defaulted to "standard" — the
                             underline Input that themeV5's `MuiInput.underline`
                             block styles. v5+ defaults to "outlined", which
                             would swap in a bordered control those overrides
                             never reach. `InputLabelProps` was removed in the
                             v9 line; `slotProps.inputLabel` is the same thing. */
                          <TextField
                            fullWidth
                            variant="standard"
                            label="First Name"
                            {...input}
                            disabled={loading}
                            type="text"
                            placeholder="First Name"
                            error={meta.error && meta.touched}
                            helperText={meta.touched && meta.error}
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} sx={formFieldItemSx}>
                      <Field name="lastName" validate={isRequired}>
                        {({ input, meta }) => (
                          <TextField
                            fullWidth
                            variant="standard"
                            label="Last Name"
                            {...input}
                            disabled={loading}
                            type="text"
                            placeholder="Last Name"
                            error={meta.error && meta.touched}
                            helperText={meta.touched && meta.error}
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12 }} sx={formFieldItemSx}>
                      <Field
                        name="email"
                        validate={composeValidators(
                          isEmail,
                          isEmailRequired,
                          isEmailDomainValid,
                        )}
                      >
                        {({ input, meta }) => (
                          <>
                            <TextField
                              fullWidth
                              variant="standard"
                              label="Email"
                              {...input}
                              onBlur={(e) => {
                                input.onBlur(e);
                                setEmailSuggestion(
                                  getEmailSuggestion(input.value),
                                );
                              }}
                              onFocus={(e) => {
                                input.onFocus(e);
                                setEmailSuggestion(null);
                              }}
                              disabled={loading}
                              type="email"
                              placeholder="Email"
                              error={meta.error && meta.touched}
                              helperText={meta.touched && meta.error}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                            />
                            {emailSuggestion && !meta.error && meta.touched && (
                              <Typography
                                variant="body2"
                                style={{
                                  color: "#f5a623",
                                  marginTop: 4,
                                  fontSize: "0.8rem",
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  input.onChange(emailSuggestion);
                                  setEmailSuggestion(null);
                                }}
                              >
                                Did you mean <strong>{emailSuggestion}</strong>?
                                Click to use it.
                              </Typography>
                            )}
                          </>
                        )}
                      </Field>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12 }} sx={formFieldItemSx}>
                      <Field
                        name="password"
                        validate={composeValidators(
                          isPasswordRequired,
                          isPasswordValid,
                        )}
                      >
                        {({ input, meta }) => (
                          <TextField
                            fullWidth
                            variant="standard"
                            label="Password"
                            {...input}
                            disabled={loading}
                            type="password"
                            placeholder="Password"
                            error={meta.error && meta.touched}
                            helperText={meta.touched && meta.error}
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                      <Button
                        name="next"
                        type="submit"
                        fullWidth
                        size="large"
                        sx={submitSx}
                        disabled={loading}
                        style={{ margin: 0 }}
                      >
                        Next
                      </Button>
                    </Grid>
                  </Grid>
                </FormBody>
              </form>
            )}
          </Form>
        )}
        {verifying && (
          <>
            <Typography variant="body2" sx={bodyTextSx} align="center">
              We found an account matching the email address you provided, for
              security reasons we need to verify you own this account. We have
              sent a 6 digit code to:
            </Typography>
            <Typography variant="body1" sx={submittedMobileNumberSx}>
              {application?.email}
            </Typography>
            <Typography
              variant="body2"
              sx={incorrectMobileNumberSx}
              // onClick={changeMobileNumber}
            >
              Incorrect email? Change it?
            </Typography>
            <Typography
              variant="body1"
              sx={bodyTextSx}
              align="center"
              style={{ fontWeight: 500 }}
            >
              Please enter the code below.
            </Typography>
            <FormElement>
              <Box sx={orphanGridItemSx}>
                <VerificationCodeInput
                  type="number"
                  fields={6}
                  onComplete={verifyEmail}
                  disabled={loading}
                />
              </Box>
              <Box sx={orphanGridItemSx}>
                <Button
                  size="medium"
                  // onClick={resendVerificationCode}
                  sx={secSubmitGoBackSx}
                  style={{ paddingTop: 0, paddingLeft: "20px" }}
                >
                  Resend Code
                </Button>
              </Box>
            </FormElement>
          </>
        )}
      </StepContainer.Body>
      <StepContainer.Actions />
    </StepContainer>
  );
};

export default SignUpStepDetails;
